import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { listPatTokens, revokePatToken, type PatSummary } from '../api';

/**
 * AOZONE-96/100: manage-tokens table on the dedicated /user/tokens page.
 * Auto-loads the caller's active MCP tokens on mount (created + expiry)
 * and renders them as a table with a per-token revoke action.
 *
 * AOZONE-105 — WHY THIS COMPONENT NEVER RE-LISTS AFTER A MUTATION.
 *
 * `GET /api/auth/pats` is backed by a Query against a DynamoDB global secondary
 * index, and a GSI **cannot** be read with `ConsistentRead`. So a refetch issued
 * immediately after a mint or a revoke can return a stale view: missing a token that
 * was just created, or still showing one that was just deleted. Both mutations
 * therefore update local state directly, and `newToken` carries a freshly minted row
 * in from the page rather than the page asking us to reload.
 *
 * THE TWO DIRECTIONS ARE DELIBERATELY ASYMMETRIC. Do not "make them consistent":
 *
 *   - Optimistic ADD on mint is SAFE. Worst case we display a row for a token we
 *     know exists, because the server just told us it created it.
 *   - Optimistic REMOVE on revoke would be UNSAFE, so we do not do it. We drop the
 *     row only AFTER the DELETE resolves 2xx. If a revoke fails and we had already
 *     removed the row, we would be CONCEALING A TOKEN THAT IS STILL LIVE — which is
 *     exactly the bug AOZONE-105 exists to eliminate, reintroduced in the UI.
 *
 * The rule behind the asymmetry: on failure, err toward SHOWING a credential, never
 * toward hiding one. A spurious row is a confusing UI; a hidden live token is an
 * unrevokable credential.
 *
 * Never displays a secret — the server stores only the hash, so the list carries
 * only an opaque `id` and metadata. A revoked token 401s on its very next MCP
 * call. The list/revoke endpoints are session-gated server-side and scoped to
 * the caller's own email (AOZONE-102), so this table can only ever show — and
 * revoke — the caller's own tokens.
 */

interface ManageMcpTokensProps {
  /** A just-minted token to show immediately, instead of re-listing. */
  newToken?: PatSummary | null;
}

function formatUnixSeconds(seconds?: number): string {
  if (seconds === undefined) return 'never';
  return new Date(seconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatIso(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function ManageMcpTokens({ newToken }: ManageMcpTokensProps = {}) {
  // Three independent facts, rather than one mutable list. Keeping them separate is
  // what makes the component ORDER-INDEPENDENT: the mount fetch and a mint can land
  // in either order without one clobbering the other. (An earlier single-array
  // version lost a just-minted token whenever the fetch resolved after the mint.)
  const [fetched, setFetched] = useState<PatSummary[] | null>(null);
  /** Tokens we know exist because the server just minted them for us. */
  const [minted, setMinted] = useState<PatSummary[]>([]);
  /** Tokens the server has CONFIRMED deleted. Never populated on a failed revoke. */
  const [revokedIds, setRevokedIds] = useState<string[]>([]);
  // Two error slots, not one. They gate the UI differently, and conflating them was
  // hiding live credentials: a FAILED REVOKE used to blank the whole table, so the
  // user was told nothing about tokens that still exist and can still authenticate.
  // A load failure means we have nothing to show; a revoke failure means we have
  // everything to show and one action didn't happen.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [mergedId, setMergedId] = useState<string | null>(null);

  // Absorb a newly minted token during RENDER (React's "adjust state when a prop
  // changes") rather than in an effect: an effect double-runs under StrictMode and
  // re-runs on remount, so it would need this same guard plus an extra render pass.
  // `mergedId` makes it happen once per token id.
  if (newToken && newToken.id !== mergedId) {
    setMergedId(newToken.id);
    setMinted((cur) => (cur.some((t) => t.id === newToken.id) ? cur : [newToken, ...cur]));
  }

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setFetched(await listPatTokens());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  // Load once on mount. Deliberately NOT re-run after a mint or revoke — see the
  // consistency note at the top of this file.
  useEffect(() => {
    void load();
  }, [load]);

  const onRevoke = async (id: string) => {
    setRevoking(id);
    setRevokeError(null);
    try {
      await revokePatToken(id);
      // ONLY after the server confirms 2xx. On failure we fall through to the catch,
      // `revokedIds` is untouched, and the row STAYS VISIBLE — never hide a token we
      // have not been told is gone.
      setRevokedIds((cur) => (cur.includes(id) ? cur : [...cur, id]));
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : String(err));
    } finally {
      setRevoking(null);
    }
  };

  // Minted-first (newest at the top, matching the server's newest-first ordering),
  // deduped by id, minus anything confirmed revoked. Deriving instead of mutating is
  // why a revoked token cannot be resurrected by a still-set `newToken` prop.
  const tokens: PatSummary[] | null =
    fetched === null && minted.length === 0
      ? null
      : [...minted, ...(fetched ?? [])]
          .filter((t, i, all) => all.findIndex((o) => o.id === t.id) === i)
          .filter((t) => !revokedIds.includes(t.id));

  return (
    <div className="w-full">
      {loadError && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          Could not load your MCP tokens ({loadError}).
        </div>
      )}

      {/* A revoke failure is reported ABOVE the table, and the table stays. The token
          still exists, so the list must keep showing it — otherwise we would be
          concealing a live credential, which is the failure AOZONE-105 exists to
          prevent. */}
      {revokeError && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          Could not revoke that token ({revokeError}). It is still active.
        </div>
      )}

      {/* "No active tokens" is a CLAIM about the full set, so it is only honest when
          the load succeeded. On a load failure we say nothing rather than implying the
          user has none. */}
      {!loadError && tokens !== null && tokens.length === 0 && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          No active tokens.
        </div>
      )}

      {/* Rows render whenever we have any, INCLUDING when the load failed: a minted
          token is one the server told us it created, and hiding it would conceal a
          live credential. The load error stays visible above, so the user knows the
          list is incomplete rather than believing it is complete. */}
      {tokens !== null && tokens.length > 0 && (
        <table className="w-full border-collapse text-left text-sm text-slate-700">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <th scope="col" className="px-4 py-2 font-medium">
                Created
              </th>
              <th scope="col" className="px-4 py-2 font-medium">
                Expiry
              </th>
              <th scope="col" className="px-4 py-2 text-right font-medium">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className="border-b border-slate-100">
                <td className="px-4 py-2">{formatIso(t.createdAt)}</td>
                <td className="px-4 py-2">{formatUnixSeconds(t.expiresAt)}</td>
                <td className="px-4 py-2 text-right">
                  <Button
                    type="button"
                    onClick={() => onRevoke(t.id)}
                    disabled={revoking === t.id}
                    variant="outline"
                    size="sm"
                  >
                    {revoking === t.id ? 'Revoking…' : 'Revoke'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
